import { useMemo } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Divider,
  Flex,
  Group,
  Menu,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconFileTypeXls } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  createMRTColumnHelper,
  MantineReactTable,
  MRT_ColumnOrderState,
  MRT_ColumnPinningState,
  MRT_ColumnSizingState,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_VisibilityState,
  useMantineReactTable,
} from "mantine-react-table";

import { useGetBookings } from "@/shared/api/generated/booking";
import { GetBookingsItem } from "@/shared/api/generated/schemas";
import { AnchorLink } from "@/shared/components/AnchorLink";
import { usePaginationSearchParamsState } from "@/shared/components/table/custom/usePaginationSearchParamsState";
import { getDefaultMRTOptions } from "@/shared/components/table/defaultMRTOptions";
import { langugeListnbNO } from "@/shared/utils/languageList";

import UpdateAdditionalInformationModal from "../update-booking/update-additional-information-modal";
import { downloadBookingsAsExcelFile } from "../utils/export-to-excel";
import { bookingStatusLabels } from "../utils/status";
import { bookingVisitCategoryLabels } from "../utils/visit-category";
import UpdateBookingGuide from "./update-booking-guide";

export default function BookingsTable({
  includeCancelled,
  museumId,
}: {
  includeCancelled: boolean | undefined;
  museumId?: string;
}) {
  const navigate = useNavigate();

  const { i18n, t } = useLingui();

  const { data: bookings, isLoading } = useGetBookings({
    includeProgram: true,
    includeCancelled: includeCancelled ?? false,
    locations: museumId ? [museumId] : undefined,
  });

  const languageMap = useMemo(
    () => new Map(langugeListnbNO.map((l) => [l.value, l.label])),
    [],
  );

  const languageFilterOptions = useMemo(() => {
    const uniqueCodes = new Set<string>();
    bookings?.data?.forEach((booking) => {
      booking.group?.languages?.forEach((code) => uniqueCodes.add(code));
    });
    return Array.from(uniqueCodes)
      .map((code) => ({
        value: code,
        label: languageMap.get(code) ?? code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "nb"));
  }, [bookings?.data, languageMap]);

  const ch = createMRTColumnHelper<GetBookingsItem>();

  const columns = [
    ch.accessor("id", {
      header: "#",
      Cell: ({ cell, renderedCellValue }) => (
        <AnchorLink
          to={
            museumId
              ? "/museum/$museumId/bookings/$bookingId"
              : "/admin/bookings/$bookingId"
          }
          params={{
            bookingId: cell.getValue().toString(),
            museumId: museumId,
          }}
        >
          {renderedCellValue}
        </AnchorLink>
      ),
    }),
    ch.accessor(
      (row) =>
        i18n.date(row.arrival, {
          dateStyle: "short",
          timeStyle: "short",
        }),
      {
        id: "arrival",
        header: t`Ankomst`,
        filterVariant: "date-range",
        // Custom sorting function is used to pick arrival date (which is ISO string) for sorting
        // but keep localized accessor for display and filtering
        sortingFn: (rowA, rowB, _columnId) => {
          const a = new Date(rowA.original.arrival);
          const b = new Date(rowB.original.arrival);

          return a === b ? 0 : a > b ? 1 : -1;
        },
        filterFn: (row, _columnId, filterValue) => {
          const [start, end] = filterValue as [
            Date | undefined | "",
            Date | undefined | "",
          ];

          // not set - Date
          if (!start && end) {
            return dayjs(row.original.arrival).isSameOrBefore(end, "day");
          }

          // Date - not set
          if (start && !end) {
            return dayjs(row.original.arrival).isSameOrAfter(start, "day");
          }

          // Date - Date
          if (start && end) {
            return dayjs(row.original.arrival).isBetween(
              start,
              end,
              "day",
              "[]",
            );
          }

          return true;
        },
      },
    ),
    ch.accessor("department.name", {
      header: t`Avdeling`,
      filterVariant: "select",
    }),
    ch.accessor("location.name", {
      header: t`Lokasjon`,
      filterVariant: "select",
    }),
    ch.accessor("group.guests.booked", {
      header: t`Antall forventet`,
      filterVariant: "range",
      Cell: ({ cell, row }) => (
        <Text size="sm">
          {cell.getValue()} + {row.original.group?.travelLeader.booked}
        </Text>
      ),
    }),
    ch.accessor(
      (row) =>
        (row.group?.arrivals?.sum ?? 0) - (row.group?.arrivals?.staff ?? 0),
      {
        header: t`Antall ankommet`,
        filterVariant: "range",
        Cell: ({ cell, row }) => (
          <Text size="sm">
            {cell.getValue()} + {row.original.group?.arrivals?.staff ?? 0}
          </Text>
        ),
      },
    ),
    ch.accessor("group.name", {
      header: t`Gruppe`,
    }),
    ch.accessor((row) => row.group?.languages ?? [], {
      id: "languages",
      header: t`Språk`,
      filterVariant: "multi-select",
      mantineFilterMultiSelectProps: {
        data: languageFilterOptions,
      },
      filterFn: (row, _columnId, filterValue: string[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        const rowLanguages = row.original.group?.languages ?? [];
        return filterValue.every((code) => rowLanguages.includes(code));
      },
      Cell: ({ row }) => {
        const languages = row.original.group?.languages;
        if (!languages || languages.length === 0) return "-";
        return languages
          .map((code) => languageMap.get(code) ?? code)
          .join(", ");
      },
    }),
    ch.accessor("customer.name", { header: t`Kunde`, filterVariant: "select" }),
    ch.accessor(
      (row) =>
        row.visitCategory
          ? i18n.t(bookingVisitCategoryLabels[row.visitCategory]) ||
            row.visitCategory
          : "-",
      {
        header: t`Type`,
        filterVariant: "select",
      },
    ),
    ch.accessor(
      (row) =>
        row.status
          ? i18n.t(bookingStatusLabels[row.status]) || row.status
          : "-",
      {
        header: t`Status`,
        filterVariant: "select",
      },
    ),
    ch.accessor(
      (row) => {
        if (row.sentToPowerOfficeDate) {
          return i18n.date(row.sentToPowerOfficeDate, {
            dateStyle: "short",
            timeStyle: "short",
          });
        }

        return "-";
      },
      {
        header: t`Sendt til PowerOffice`,
      },
    ),
    ch.accessor(
      (row) =>
        row.guide?.firstName
          ? `${row.guide?.firstName} ${row.guide?.lastName ?? ""}`
          : "-",
      {
        header: t`Guide`,
        Cell: ({ row }) => <UpdateBookingGuide booking={row.original} />,
      },
    ),
    ch.display({
      id: "comment",
      header: "",
      Cell: ({ row }) => (
        <UpdateAdditionalInformationModal booking={row.original} />
      ),
      enableColumnActions: false,
      enableResizing: false,
      size: 60,
      mantineTableHeadCellProps: {
        align: "right",
      },
      mantineTableBodyCellProps: {
        align: "right",
      },
    }),
  ];

  const [columnSizing, setColumnSizing] =
    useLocalStorage<MRT_ColumnSizingState>({
      key: "table:bookings-table:column-sizing",
      defaultValue: {},
    });

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<MRT_VisibilityState>({
      key: "table:bookings-table:column-visibility",
      defaultValue: {},
    });

  const [columnOrder, setColumnOrder] = useLocalStorage<MRT_ColumnOrderState>({
    key: "table:bookings-table:column-order",
    getInitialValueInEffect: false,
    defaultValue: [],
  });

  const [columnPinning, setColumnPinning] =
    useLocalStorage<MRT_ColumnPinningState>({
      key: "table:bookings-table:column-pinning",
      defaultValue: {},
    });

  const [pagination, setPagination] = usePaginationSearchParamsState();

  const defaultMRTOptions = getDefaultMRTOptions<GetBookingsItem>();

  const table = useMantineReactTable({
    ...defaultMRTOptions,
    columns,
    data: useMemo(() => bookings?.data ?? [], [bookings?.data]),
    initialState: {
      ...defaultMRTOptions.initialState,
      showColumnFilters: true,
      sorting: [
        {
          id: "arrival",
          desc: true,
        },
      ],
    },
    enableFacetedValues: true,
    enableGlobalFilterRankedResults: false,
    renderTopToolbar: ({ table }) => (
      <>
        <Flex justify="space-between" p="md">
          <Group gap="xs">
            <MRT_GlobalFilterTextInput variant="default" mx={0} table={table} />
            <Switch
              label="Inkluder avlyste"
              checked={includeCancelled ?? false}
              onChange={(event) =>
                navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    includeCancelled: event.currentTarget.checked,
                  }),
                })
              }
            />
          </Group>

          <Group gap="xs">
            <Tooltip label={t`Eksporter gjeldende data til Excel`}>
              <ActionIcon
                color="gray"
                size="lg"
                variant="subtle"
                onClick={() =>
                  downloadBookingsAsExcelFile(
                    table.getPrePaginationRowModel().rows,
                  )
                }
              >
                <IconFileTypeXls />
              </ActionIcon>
            </Tooltip>
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
          </Group>
        </Flex>
        <Divider />
      </>
    ),
    enableColumnResizing: true,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnOrdering: true,
    onColumnOrderChange: setColumnOrder,
    enableColumnPinning: true,
    onColumnPinningChange: setColumnPinning,
    enablePagination: true,
    onPaginationChange: setPagination,
    state: {
      isLoading,
      columnSizing,
      columnVisibility,
      columnOrder,
      columnPinning,
      pagination,
    },
    enableRowActions: true,
    renderRowActionMenuItems: ({ row }) => (
      <>
        <Link
          to={
            museumId
              ? "/museum/$museumId/bookings/$bookingId"
              : "/admin/bookings/$bookingId"
          }
          params={{ bookingId: row.original.id.toString(), museumId: museumId }}
          style={{ textDecoration: "none" }}
        >
          <Menu.Item>
            <Trans>Endre</Trans>
          </Menu.Item>
        </Link>
        {/* <Menu.Item onClick={() => console.info("Delete")}>Delete</Menu.Item> */}
      </>
    ),
  });

  return <MantineReactTable table={table} />;
}
